import { z } from 'zod/v4'
import { paginationLinksSchema, paginationMetaSchema } from '@/lib/schema/v2/pagination'
import { companySchema } from '@/lib/schema/v2/company'
import { ticketDepartmentSchema } from '@/lib/schema/v2/ticket-department'
import { ticketTypeSchema } from '@/lib/schema/v2/ticket-type'
import { companyContactSchema } from '@/lib/schema/v2/company-contact'
import { userSchema } from '@/lib/schema/v2/user'
import { zDayJs } from '@/lib/schema/v2/dayjs'
import { ampApiErrorSchema } from '@/lib/schema/v2/amp-api-response'
import { ticketPrioritySchema } from '@/lib/schema/v2/ticket-priority'
import { ticketResourcesSchema } from '@/lib/schema/v2/ticket-resource'
import { ticketStatusSchema } from '@/lib/schema/v2/ticket-status'
import { logsSchema } from '@/lib/schema/v2/log'

export const fieldsForTable =
    'uuid,title,description,number,staff_only,created_at,updated_at,ticket_priority{uuid,color,title},ticket_company{uuid,title},ticket_company_contact{uuid,first_name,last_name},ticket_department{uuid,title},ticket_type{uuid,title},ticket_resources{uuid,assignee_uuid,ticket_resource_assignee{uuid,full_name,profile_photo_url}},ticket_status{uuid,title}'

export const fieldsForDetails =
    'uuid,title,description,number,staff_only,staff_notes,resolution,created_at,updated_at,ticket_priority{uuid,color,title},ticket_company{uuid,title,company_contacts{email}},ticket_company_contact{uuid,first_name,last_name,email},ticket_department{uuid,title},ticket_type{uuid,title},ticket_resources{uuid,assignee_uuid,assignee_type,is_primary},ticket_status{uuid,title}'

export const sourceSchema = z.object({
    created_at: zDayJs,
    title: z.string(),
    updated_at: zDayJs,
    uuid: z.uuid(),
})

export const ticketSchema = z.object({
    get children() {
        return z.array(ticketSchema).nullish()
    },
    company: companySchema.nullish(),
    company_contact: companyContactSchema.nullish(),
    company_contact_uuid: z.uuid().nullish(),
    company_uuid: z.uuid().nullish(),
    creator: userSchema.nullish(),
    creator_type: z.string().nullish(),
    creator_uuid: z.uuid().nullish(),
    created_at: zDayJs.nullish(),
    department: ticketDepartmentSchema.nullish(),
    department_uuid: z.uuid().nullish(),
    description: z.string().nullish(),
    description_text: z.string().nullish(),
    description_format: z.string().nullish(),
    foreign_id: z.string().nullish(),
    staff_only: z.boolean().nullish(),
    logs: logsSchema.nullish(),
    message_id: z.string().nullish(),
    number: z.number().nullish(),
    parent_uuid: z.uuid().nullish(),
    priority: ticketPrioritySchema.nullish(),
    priority_uuid: z.uuid().nullish(),
    resolution: z.string().nullish(),
    resources: z.lazy(() => ticketResourcesSchema.nullish()),
    staff_notes: z.string().nullish(),
    status: ticketStatusSchema.nullish(),
    status_uuid: z.uuid().nullish(),
    source: sourceSchema.nullish(),
    source_type: z.string().nullish(),
    source_uuid: z.uuid().nullish(),
    title: z.string().nullish(),
    title_with_number: z.string().nullish(),
    type: ticketTypeSchema.nullish(),
    type_uuid: z.uuid().nullish(),
    updated_at: zDayJs.nullish(),
    uuid: z.uuid().nullish(),
})

export const ticketsSchema = z.array(ticketSchema)

export const paginatedTicketsResponseSchema = z.object({
    data: ticketsSchema.nullish(),
    links: paginationLinksSchema.nullish(),
    meta: paginationMetaSchema.nullish(),
    success: z.boolean(),
    error: ampApiErrorSchema.nullish(),
})

export const retrieveTicketResponseSchema = z.object({
    data: ticketSchema.nullish(),
    success: z.boolean(),
    error: ampApiErrorSchema.nullish(),
})

export const updateTicketResponseSchema = z.object({
    data: ticketSchema.nullish(),
    success: z.boolean(),
    error: ampApiErrorSchema.nullish(),
})

export const createTicketResponseSchema = z.object({
    data: ticketSchema.nullish(),
    success: z.boolean(),
    error: ampApiErrorSchema.nullish(),
})

export const ticketUpdateFormSchema = z.object({
    ticket_uuid: z.uuid(),
    title: z.string(),
    parent_uuid: z.uuid().nullable(),
})

export const generateAiReplyResponseSchema = z.object({
    data: z
        .object({
            reply: z.string(),
        })
        .nullish(),
    success: z.boolean(),
    error: ampApiErrorSchema.nullish(),
})

const validation_requiredMessage = "You can't leave this empty."

export const ticketCreateFormSchema = z.object({
    company_uuid: z.uuid({
        error: validation_requiredMessage,
    }),
    company_contact_uuid: z
        .uuid({
            error: validation_requiredMessage,
        })
        .nullable(),
    department_uuid: z.uuid({
        error: validation_requiredMessage,
    }),
    description: z
        .string({
            error: validation_requiredMessage,
        })
        .min(1, {
            message: validation_requiredMessage,
        }),
    description_format: z
        .string({
            error: validation_requiredMessage,
        })
        .min(1, {
            message: validation_requiredMessage,
        }),
    primary_resource_type: z
        .string({
            error: validation_requiredMessage,
        })
        .min(1, {
            message: validation_requiredMessage,
        }),
    primary_resource_uuid: z.uuid({
        error: validation_requiredMessage,
    }),
    priority_uuid: z.uuid({
        error: validation_requiredMessage,
    }),
    staff_only: z.boolean(),
    status_uuid: z.uuid({
        error: validation_requiredMessage,
    }),
    type_uuid: z.uuid({
        error: validation_requiredMessage,
    }),
    title: z
        .string({
            error: validation_requiredMessage,
        })
        .min(1, {
            message: validation_requiredMessage,
        }),
})

export type PaginatedTicketsResponse = z.infer<typeof paginatedTicketsResponseSchema>
export type Ticket = z.infer<typeof ticketSchema>
export type Tickets = z.infer<typeof ticketsSchema>
export type TicketUpdateForm = z.infer<typeof ticketUpdateFormSchema>
export type TicketCreateForm = z.infer<typeof ticketCreateFormSchema>
export type RetrieveTicketResponse = z.infer<typeof retrieveTicketResponseSchema>
export type UpdateTicketResponse = z.infer<typeof updateTicketResponseSchema>
export type GenerateAiReplyResponse = z.infer<typeof generateAiReplyResponseSchema>
